/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';
import {
    ApproverScope,
    blockMainActionVar,
    CreateTemplateInstanceWizardSteps,
    currentStepVar,
    templateInstanceAllMustRespondVar,
    templateInstanceApproverRouteTypeVar,
    templateInstanceApproverScopeVar,
    templateInstanceApproversVar,
    templateInstanceCustomResponseOptionsVar,
    templateInstanceDynamicRouteVar,
    templateInstanceIsCustomResponseEnabledVar,
    templateInstanceIsFileAttachmentRequiredVar,
    templateInstanceStaticRouteVar,
    templateInstanceWorkflowSchemaVersionVar,
    templateInstanceFallbackPolicyVar,
    templateInstanceIsApproverCommentsRequiredVar,
    templateInstanceCategoryVar,
    templateInstanceStepRoutesVar,
} from '../../../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { Constants } from '../../../../../common/Constants';
import {
    RouteTypes,
    WorkflowSchemaInput,
    WorkflowSchema,
    ApprovalTemplateCategory,
    StepRouteSetting,
} from '../../../../../service/gen/templates';
import {
    PeopleResults2UserIds,
    UI_StaticRouteInput2StaticRoute,
    UI_StaticRouteInput2StaticRouteInput,
    UI_FallbackPolicyInput2FallbackPolicyInput,
    UI_FallbackPolicyInput2FallbackPolicy,
    MixedRouteType,
    UI_DynamicRouteInput2DynamicRouteInput,
    UI_DynamicRouteInput2DynamicRoute,
    UI_StepRouteSettingInput2StepsRouteInput,
} from '../../../../../utilities/TemplateUtils';
import { useTemplateInstanceApproverSettingsV1 } from './TemplateApproverSettings/TemplateApproverSettingsV1';
import { useTemplateInstanceApproverSettingsV2 } from './TemplateApproverSettings/TemplateApproverSettingsV2';
import { UI_StepRouteSettingInput } from './TemplateApproverSettings/TemplateSequentialApproverSettings';
import { WorkflowSettingsComponent, WorkflowSettingsComponentProps } from './WorkflowSettings.component';

export const WorkflowSettingsContainer: React.FC = (): JSX.Element => {
    const maxCustomResponseOptions = Constants.MAX_CUSTOM_RESPONSE_OPTIONS;
    const requiredCustomResponseOptions = Constants.REQUIRED_CUSTOM_RESPONSE_OPTIONS;

    const workflowSchemaVersion = useReactiveVar(templateInstanceWorkflowSchemaVersionVar);
    const isCustomResponsesEnabled = useReactiveVar(templateInstanceIsCustomResponseEnabledVar);
    const customResponsesFields = useReactiveVar(templateInstanceCustomResponseOptionsVar);
    const currentStep = useReactiveVar(currentStepVar);
    const isFileAttachmentRequired = useReactiveVar(templateInstanceIsFileAttachmentRequiredVar);
    const isApproverCommentsRequired = useReactiveVar(templateInstanceIsApproverCommentsRequiredVar);

    const templateInstanceApproverSettingsHook = React.useMemo((): (() => any) => {
        switch (workflowSchemaVersion) {
            case 'v1.0':
                return useTemplateInstanceApproverSettingsV1;
            case 'v2.0':
                return useTemplateInstanceApproverSettingsV2;
            default:
                throw new Error('invalid workflow schema version');
        }
    }, [workflowSchemaVersion]);
    const templateInstanceApproverSettingsProps = templateInstanceApproverSettingsHook();
    const { templateInstanceApproversValid } = templateInstanceApproverSettingsProps;

    React.useEffect((): void => {
        // Template instance validation
        if (currentStep === CreateTemplateInstanceWizardSteps.WorkflowSettings) {
            const customResponseHasError =
                isCustomResponsesEnabled &&
                (!customResponsesFields ||
                    customResponsesFields.length < requiredCustomResponseOptions ||
                    customResponsesFields.slice(0, requiredCustomResponseOptions).some((field: string): boolean => {
                        return !!!field;
                    }) ||
                    // check if user input only whitespace
                    customResponsesFields.some((field: string): boolean => {
                        return field.trim() === '' && field !== '';
                    }));

            if (customResponseHasError || !templateInstanceApproversValid) {
                blockMainActionVar(true);
            } else {
                blockMainActionVar(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isCustomResponsesEnabled,
        customResponsesFields,
        currentStep,
        requiredCustomResponseOptions,
        templateInstanceApproversValid,
    ]);

    const category = useReactiveVar(templateInstanceCategoryVar);
    React.useEffect((): void => {
        // Attachment Required for Word category approvals
        if (category === ApprovalTemplateCategory.WordAddin) {
            templateInstanceIsFileAttachmentRequiredVar(true);
        }
    }, [category]);

    const onCustomResponsesToggled = (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean): void => {
        templateInstanceIsCustomResponseEnabledVar(checked);
    };

    const onCustomResponsesFieldsChange = (responses: string[]): void => {
        templateInstanceCustomResponseOptionsVar(responses);
    };

    const onIsFileAttachmentRequiredChange = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        checked?: boolean
    ): void => {
        templateInstanceIsFileAttachmentRequiredVar(checked);
    };

    const onIsApproverCommentRequiredChange = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        checked?: boolean
    ): void => {
        templateInstanceIsApproverCommentsRequiredVar(checked);
    };

    const componentProps: WorkflowSettingsComponentProps = {
        ...templateInstanceApproverSettingsProps,
        workflowSchemaVersion,
        maxCustomResponseOptions,
        requiredCustomResponseOptions,
        isCustomResponsesEnabled,
        onCustomResponsesToggled,
        onCustomResponsesFieldsChange,
        customResponsesFields,
        isWordCategoryTemplate: category === ApprovalTemplateCategory.WordAddin,
        isFileAttachmentRequired,
        onIsFileAttachmentRequiredChange,
        showApproverCommentsRequired: true,
        isApproverCommentsRequired,
        onIsApproverCommentRequiredChange,
    };

    return <WorkflowSettingsComponent {...componentProps} />;
};

export const useTemplateInstanceWorkflowSchemaInput = (): WorkflowSchemaInput => {
    // Vars fow workflow schema v1.0 templat instance approvers settings.
    const workflowSchemaVersion = useReactiveVar(templateInstanceWorkflowSchemaVersionVar);
    const approverScope = useReactiveVar(templateInstanceApproverScopeVar);
    const approvers = useReactiveVar(templateInstanceApproversVar);
    const allMustRespond = useReactiveVar(templateInstanceAllMustRespondVar);
    // Vars fow workflow schema v2.0 templat instance approvers settings.
    const approverRouteType = useReactiveVar(templateInstanceApproverRouteTypeVar);
    const staticRoute = useReactiveVar(templateInstanceStaticRouteVar);
    const dynamicRoute = useReactiveVar(templateInstanceDynamicRouteVar);
    const fallbackPolicy = useReactiveVar(templateInstanceFallbackPolicyVar);
    const stepRoutes = useReactiveVar(templateInstanceStepRoutesVar);
    const isCustomResponseEnabled = useReactiveVar(templateInstanceIsCustomResponseEnabledVar);
    const customResponseOptions = useReactiveVar(templateInstanceCustomResponseOptionsVar)?.map(
        (option: string): string => {
            return option?.trim();
        }
    );
    switch (workflowSchemaVersion) {
        case 'v1.0': {
            const workflowSchema: WorkflowSchemaInput = {
                Version: workflowSchemaVersion,
                Approvers: approverScope === ApproverScope.SpecifyApprovers ? PeopleResults2UserIds(approvers) : [],
                AllMustRespond: allMustRespond,
                IsCustomResponseEnabled: isCustomResponseEnabled,
                CustomResponseOptions: isCustomResponseEnabled ? customResponseOptions : [],
            };
            return workflowSchema;
        }
        case 'v2.0': {
            const workflowSchema: WorkflowSchemaInput = {
                Version: workflowSchemaVersion,
                RouteTypes: approverRouteType,
                StaticRoute:
                    approverRouteType === RouteTypes.StaticRoute || approverRouteType === MixedRouteType
                        ? UI_StaticRouteInput2StaticRouteInput(staticRoute)
                        : undefined,
                DynamicRoute:
                    approverRouteType === RouteTypes.DynamicRoute || approverRouteType === MixedRouteType
                        ? UI_DynamicRouteInput2DynamicRouteInput(dynamicRoute)
                        : undefined,
                FallbackPolicy:
                    approverRouteType === RouteTypes.DynamicRoute
                        ? UI_FallbackPolicyInput2FallbackPolicyInput(fallbackPolicy)
                        : undefined,
                IsCustomResponseEnabled: isCustomResponseEnabled,
                CustomResponseOptions: isCustomResponseEnabled ? customResponseOptions : [],
                AllMustRespond: null,
                StepRoutes: UI_StepRouteSettingInput2StepsRouteInput(stepRoutes),
            };
            return workflowSchema;
        }
        default:
            throw new Error('invalid workflow schema version in template');
    }
};

export const useTemplateInstanceWorkflowSchemaPreview = (): WorkflowSchema => {
    // Vars fow workflow schema v1.0 templat instance approvers settings.
    const workflowSchemaVersion = useReactiveVar(templateInstanceWorkflowSchemaVersionVar);
    const approverScope = useReactiveVar(templateInstanceApproverScopeVar);
    const approvers = useReactiveVar(templateInstanceApproversVar);
    const allMustRespond = useReactiveVar(templateInstanceAllMustRespondVar);
    // Vars fow workflow schema v2.0 templat instance approvers settings.
    const approverRouteType = useReactiveVar(templateInstanceApproverRouteTypeVar);
    const staticRoute = useReactiveVar(templateInstanceStaticRouteVar);
    const dynamicRoute = useReactiveVar(templateInstanceDynamicRouteVar);
    const fallbackPolicy = useReactiveVar(templateInstanceFallbackPolicyVar);

    const isCustomResponseEnabled = useReactiveVar(templateInstanceIsCustomResponseEnabledVar);
    const customResponseOptions = useReactiveVar(templateInstanceCustomResponseOptionsVar)?.map(
        (option: string): string => {
            return option?.trim();
        }
    );
    const stepRoutes = useReactiveVar(templateInstanceStepRoutesVar);
    switch (workflowSchemaVersion) {
        case 'v1.0': {
            const workflowSchema: WorkflowSchema = {
                Version: workflowSchemaVersion,
                Approvers: approverScope === ApproverScope.SpecifyApprovers ? approvers : [],
                AllMustRespond: allMustRespond,
                IsCustomResponseEnabled: isCustomResponseEnabled,
                CustomResponseOptions: isCustomResponseEnabled ? customResponseOptions : [],
            };
            return workflowSchema;
        }
        case 'v2.0': {
            const workflowSchema: WorkflowSchema = {
                Version: workflowSchemaVersion,
                RouteTypes: approverRouteType,
                StaticRoute:
                    approverRouteType === RouteTypes.StaticRoute || approverRouteType === MixedRouteType
                        ? UI_StaticRouteInput2StaticRoute(staticRoute)
                        : undefined,
                DynamicRoute:
                    approverRouteType === RouteTypes.DynamicRoute || approverRouteType === MixedRouteType
                        ? UI_DynamicRouteInput2DynamicRoute(dynamicRoute)
                        : undefined,
                FallbackPolicy: UI_FallbackPolicyInput2FallbackPolicy(fallbackPolicy),
                IsCustomResponseEnabled: isCustomResponseEnabled,
                CustomResponseOptions: isCustomResponseEnabled ? customResponseOptions : [],
                AllMustRespond: null,
                StepRoutes: stepRoutes?.map((stepRoute: UI_StepRouteSettingInput): StepRouteSetting => {
                    return {
                        RouteTypes: stepRoute.approverRouteType,
                        StaticRoute: UI_StaticRouteInput2StaticRoute(stepRoute.staticRoute),
                        DynamicRoute: UI_DynamicRouteInput2DynamicRoute(stepRoute.dynamicRoute),
                        FallbackPolicy: UI_FallbackPolicyInput2FallbackPolicy(stepRoute.fallbackPolicy),
                    };
                }),
            };
            return workflowSchema;
        }
        default:
            throw new Error('invalid workflow schema version in template');
    }
};
